/**
 * Newsletter Service
 * 
 * This service handles newsletter subscriptions using client-side localStorage
 * as a simple alternative to server-based solutions like Klaviyo.
 */

// Storage key for subscribers in localStorage
const SUBSCRIBERS_KEY = 'substans_newsletter_subscribers';

/**
 * Check if localStorage is available
 */
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Load existing subscribers from localStorage
 */
function loadSubscribers(): { subscribers: Array<{ email: string, subscribed_at: string, source: string }> } {
  try {
    if (!isLocalStorageAvailable()) {
      return { subscribers: [] };
    }
    
    const data = localStorage.getItem(SUBSCRIBERS_KEY);
    if (!data) {
      return { subscribers: [] };
    }
    return JSON.parse(data);
  } catch (error) {
    console.error('Error loading subscribers from localStorage:', error);
    return { subscribers: [] };
  }
}

/**
 * Save subscribers to localStorage
 */
function saveSubscribers(data: { subscribers: Array<{ email: string, subscribed_at: string, source: string }> }): boolean {
  try {
    if (!isLocalStorageAvailable()) {
      return false;
    }
    
    localStorage.setItem(SUBSCRIBERS_KEY, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving subscribers to localStorage:', error);
    return false;
  }
}

/**
 * Export subscribers as a CSV file
 */
export function exportSubscribersToCSV(): void {
  const data = loadSubscribers();
  
  if (data.subscribers.length === 0) {
    alert('No subscribers to export');
    return;
  }
  
  // Create CSV content
  const csvContent = [
    // CSV header
    ['Email', 'Subscribed At', 'Source'].join(','),
    // CSV rows
    ...data.subscribers.map(subscriber => {
      return [
        subscriber.email,
        subscriber.subscribed_at,
        subscriber.source
      ].join(',');
    })
  ].join('\n');
  
  // Create a blob and download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  link.setAttribute('href', url);
  link.setAttribute('download', `substans_subscribers_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Subscribe a user to our newsletter
 * 
 * @param email - User's email address
 * @param additionalProperties - Optional additional user properties
 * @returns Promise with the result of the subscription
 */
export async function subscribeToNewsletter(
  email: string,
  _additionalProperties: Record<string, any> = {}
): Promise<{ success: boolean; message: string }> {
  try {
    if (!email) {
      return { success: false, message: 'Email is required' };
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { success: false, message: 'Please enter a valid email address' };
    }

    // Load existing subscribers
    const data = loadSubscribers();
    
    // Check if email already exists
    if (data.subscribers.some(subscriber => subscriber.email.toLowerCase() === email.toLowerCase())) {
      return { 
        success: true, 
        message: 'You are already subscribed to our newsletter!' 
      };
    }
    
    // Add new subscriber
    data.subscribers.push({
      email,
      subscribed_at: new Date().toISOString(),
      source: "substans.art website"
    });
    
    // Save updated subscribers list
    if (!saveSubscribers(data)) {
      return { 
        success: false, 
        message: 'Failed to save your subscription. Please try again later.' 
      };
    }

    // Success!
    return { 
      success: true, 
      message: 'Successfully subscribed to the newsletter!' 
    };
  } catch (error) {
    console.error('Error in newsletter subscription process:', error);
    return { 
      success: false, 
      message: 'An unexpected error occurred. Please try again later.'
    };
  }
}
